class InputWithCircles extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.hasSelect = this.hasAttribute('data-select');
    this.createDecorations();
    
    if (this.hasSelect) {
      requestAnimationFrame(() => {
        this.setupSelectInputSync();
      });
    }
  }

  createDecorations() {
    const circlePositions = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
    circlePositions.forEach(position => {
      const circle = document.createElement('div');
      circle.classList.add('circle', position);
      this.appendChild(circle);
    });

    const linePositions = ['top-line', 'right-line', 'bottom-line', 'left-line'];
    linePositions.forEach(position => {
      const line = document.createElement('div');
      line.classList.add('line', position);
      this.appendChild(line);
    });
  }

  setupSelectInputSync() {
    const select = this.querySelector('select');
    const input = this.querySelector('input');
    
    if (select && input) {
      if (select.selectedIndex >= 0) {
        input.value = select.options[select.selectedIndex].textContent;
      }
      select.addEventListener('change', () => {
        input.value = select.options[select.selectedIndex].textContent;
      });
    } else {
    }
  }
}

/**
 * Autonomous custom element that wraps the product form.
 * Extends HTMLElement (not HTMLFormElement) so it works in Safari, which
 * does not support customized built-in elements (<form is="product-form">).
 * Data attributes live on <product-form>; the inner <form> is a plain form.
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.variantIds = [];
    this.variantOptions = [];
    this.variantAvailability = {};
  }

  connectedCallback() {
    // Inner <form> is not in the DOM yet when connectedCallback runs (parser
    // invokes it before parsing the element's children). Defer init to the next task.
    setTimeout(() => {
      this._initForm();
    }, 0);
  }

  _initForm() {
    this.form = this.querySelector('form');
    if (!this.form) {
      console.warn('ProductForm: no inner form found');
      return;
    }

    try {
      this.variantIds = JSON.parse(this.dataset.variants || '[]');
      this.variantOptions = JSON.parse(this.dataset.options || '[]');
      this.variantAvailability = JSON.parse(this.dataset.availability || '{}');
    } catch (e) {
      console.warn('ProductForm: failed to parse data attributes', e);
      return;
    }

    this.form.addEventListener('change', this.handleChange.bind(this));

    this.updateVariantId();
  }

  handleChange(e) {
    if (
      e.target.matches('input[type=radio]') ||
      e.target.matches('select')
    ) {
      this.updateVariantId();
    }
  }

  updateVariantId() {
    const selectedOptions = [];

    const checkedRadios = this.querySelectorAll('input[type=radio]:checked');
    checkedRadios.forEach((radio) => {
      selectedOptions.push((radio.value || '').trim());
    });

    const selects = this.querySelectorAll('select');
    let selectionRequired = false;
    selects.forEach((select) => {
      const value = (select.value || '').trim();
      if (select.hasAttribute('data-requires-selection') && !value) {
        selectionRequired = true;
      }
      selectedOptions.push(value);
    });

    // A required option (currently Size) is intentionally unselected on load.
    // Keep the Shopify variant id empty and Add to Cart disabled until the
    // customer explicitly chooses a value.
    if (selectionRequired) {
      const variantInput = this.querySelector('.product-variant-id');
      if (variantInput) {
        variantInput.value = '';
        variantInput.setAttribute('value', '');
      }
      this.updateAddToCartButton(null, true);
      return;
    }

    const matchingVariantIndex = this.variantOptions.findIndex((variantOpts) => {
      const match = Array.isArray(variantOpts) && variantOpts.every((opt, i) => String(opt).trim() === selectedOptions[i]);
      return match;
    });

    if (matchingVariantIndex === -1) {
      console.warn('ProductForm: no matching variant for', selectedOptions, 'against', this.variantOptions);
      return;
    }

    const variantId = this.variantIds[matchingVariantIndex];
    const variantInput = this.querySelector('.product-variant-id');

    if (variantInput) {
      const value = String(variantId);
      variantInput.value = value;
      variantInput.setAttribute('value', value);
    }

    this.updateAddToCartButton(variantId);
  }

  updateAddToCartButton(variantId, selectionRequired = false) {
    const addToCartBtn = this.querySelector('.add-to-cart');
    const addToCartText = this.querySelector('.add-to-cart-text');

    if (addToCartBtn) {
      if (selectionRequired) {
        addToCartBtn.disabled = true;
        addToCartBtn.classList.remove('sold-out');
        if (addToCartText) addToCartText.textContent = 'Add to cart';
      } else if (!this.variantAvailability[variantId]) {
        addToCartBtn.disabled = true;
        addToCartBtn.classList.add('sold-out');
        if (addToCartText) addToCartText.textContent = 'Sold out';
      } else {
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove('sold-out');
        if (addToCartText) addToCartText.textContent = 'Add to cart';
      }
    }
  }
}

customElements.define('input-with-circles', InputWithCircles);
customElements.define('product-form', ProductForm);